import "./style.scss";

import type { UserVariables } from "components/shared/AgreementModal";
import type { IChannel } from "containers/Channels/types";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString } from "helpers/utils";
import type { SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Agreement, AgreementSignatureStatus } from "store/agreementsStore";
import { useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { IUser } from "types";

import { useAgreementPageContext } from "../context/StateProvider";
import type { AgreementValidatorExtended, MissingVariable } from "../utils";
import AttachedDocuments from "./AttachedDocuments";
import PanelSigners from "./PanelSigners";
import PanelValidators from "./PanelValidators";
import PanelVariables from "./PanelVariables";

interface IContractValidationRightPanel {
    /**
     * Related fetct term or article status
     */

    relatedResourceStatus: TFetchStatus;
    /**
     * Verify if agreement has validators
     */
    hasValidators: boolean;
    validators: AgreementValidatorExtended[];
    agreementStatus: TFetchStatus;
    /**
     * Function called when editing variables
     */
    handleVariableChange: (variable?: UserVariables) => void;
    /**
     * Agreement creator
     */
    owner: IUser | undefined;
    /**
     * Found user variables
     */
    userVariables: UserVariables | undefined;
    /**
     * Set user variables state
     */
    setUserVariables: React.Dispatch<SetStateAction<UserVariables | undefined>>;
    /**
     * Check if agreement uses multi-signature
     */
    hasMultiSignature: boolean;
    /**
     * Empty variables
     */
    missingVariables: MissingVariable[];
    /**
     * channels data
     */
    channels: IChannel[];
    /**
     * Array of beneficiaries missing variables
     */
    beneficiariesMissingVariables: MissingVariable[];
}

const ContractValidationRightPanel = ({
    relatedResourceStatus,
    hasValidators,
    validators,
    agreementStatus,
    handleVariableChange,
    owner,
    userVariables,
    setUserVariables,
    hasMultiSignature,
    missingVariables,
    channels,
    beneficiariesMissingVariables,
}: IContractValidationRightPanel) => {
    const { agreementDocuments } = useAppSelector((state) => state.agreements);
    const { agreement, isDraft, isOwner } = useAgreementPageContext();

    const { agreementCommentsStatus, agreementDocumentsStatus } = useAppSelector(
        (state) => state.agreements.agFetchStatus,
    );
    const { rightPanelRef } = useAgreementPageContext();
    const showDocuments = isDraft || (!isDraft && agreementDocuments.length > 0);
    const isSigned = agreement?.signedStatus === AgreementSignatureStatus.SIGNED;
    const resentAt = agreement?.resentAt
        ? formatLocaleDateString({ date: agreement.resentAt, options: { numeric: true, timeZone: "UTC" } })
        : null;
    const [t] = useTranslation();

    return (
        <div className="contract-validation-edit-right-panel" ref={rightPanelRef}>
            <PanelValidators
                termStatus={relatedResourceStatus}
                hasValidators={hasValidators}
                validators={validators}
                owner={owner}
            />
            {agreement && <PanelSigners agreement={agreement} relatedResourceStatus={relatedResourceStatus} />}
            {showDocuments && (
                <AttachedDocuments
                    agreement={agreement}
                    isOwner={isOwner}
                    isDraft={isDraft}
                    agreementDocuments={agreementDocuments}
                    agreementDocumentsStatus={agreementDocumentsStatus}
                />
            )}
            {isDraft && (
                <PanelVariables
                    agreementStatus={agreementStatus}
                    isDraft={isDraft}
                    agreement={agreement}
                    handleVariableChange={handleVariableChange}
                    isOwner={isOwner}
                    userVariables={userVariables}
                    setUserVariables={setUserVariables}
                    hasMultiSignature={hasMultiSignature}
                    missingVariables={missingVariables}
                    beneficiariesMissingVariables={beneficiariesMissingVariables}
                    channels={channels}
                />
            )}
            {resentAt && !isSigned && agreementStatus === "succeeded" && (
                <SmallBody color="grey" style={{ fontSize: "11px" }}>
                    {t("ContractValidationEdit.ResentAt", { resentAt })}
                </SmallBody>
            )}
            <div className="-section">
                {agreementCommentsStatus === "succeeded" && (
                    <div className="contract-validation-edit-right-panel-comments">
                        {/* DO NOT WRITE ANYTHING HERE, it gets populated with comments from the CommentPlugin file */}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContractValidationRightPanel;
